/**
 * SyncStatusBar — displays Celery task progress while sync is in flight.
 *
 * Renders only when summary is not "idle". Shows:
 * - Indeterminate progress bar while syncing
 * - Current stage label (e.g. "Fetching organizations…")
 * - Auto-dismisses 4s after sync completes ("done")
 *
 * Accessibility: role="status" + aria-live="polite" announces stage changes.
 */

import { useSyncStatus } from "@/stores/sync-status";
import { Progress } from "@/components/ui/progress";

export function SyncStatusBar() {
  const { summary, jobs, barVisible } = useSyncStatus();

  if (!barVisible) {
    return null;
  }

  // Find the running or most recent job for label
  const runningJob = jobs.find((j) => j.status === "running");
  const lastJob = jobs[jobs.length - 1];
  const currentJob = runningJob || lastJob;
  const stageLabel = currentJob?.stage || "Syncing...";

  // Status color/message
  let statusText = "Syncing…";
  let progressIndeterminate = true;
  if (summary === "error") {
    statusText = "Sync error";
    progressIndeterminate = false;
  } else if (summary === "done") {
    statusText = "Sync complete";
    progressIndeterminate = false;
  }

  return (
    <output
      className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 rounded"
      aria-live="polite"
    >
      <Progress
        value={progressIndeterminate ? undefined : 100}
        className="flex-1"
      />
      <div className="text-sm font-medium text-blue-900">
        {summary === "syncing" ? stageLabel : statusText}
      </div>
    </output>
  );
}
